import apiClient from "../../../api/apiClient"; // Import the centralized API client

const ACTIVITY_API_URL = "/activities"; // Base endpoint for activities

/**
 * Handles API errors gracefully.
 * @param {Error} error - The error object.
 * @returns {void}
 */
const handleApiError = (error) => { 
  console.error("API Error:", error?.response?.data || error.message);
  throw new Error(error?.response?.data?.message || "An error occurred. Please try again.");
};

/**
 * Creates a new activity for a specific module (Lead, Opportunity, Sales, etc.).
 * @param {Object} activityData - The activity details.
 * @returns {Promise<Object>} - The created activity.
 */
export const createActivity = async (activityData) => {
  try {
    const response = await apiClient.post(`${ACTIVITY_API_URL}/create`, activityData);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

/**
 * Updates an existing activity.
 * @param {string} activityId - The ID of the activity to update.
 * @param {Object} updatedData - The updated activity data.
 * @returns {Promise<Object>} - The updated activity.
 */
export const updateActivity = async (activityId, updatedData) => {
  try {
    const response = await apiClient.put(`${ACTIVITY_API_URL}/update/${activityId}`, updatedData);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

/**
 * Deletes an activity.
 * @param {string} activityId - The ID of the activity to delete.
 * @returns {Promise<Object>} - The deletion response.
 */
export const deleteActivity = async (activityId) => {
  try {
    const response = await apiClient.delete(`${ACTIVITY_API_URL}/delete/${activityId}`);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

/**
 * Fetches all activities for a specific module (Lead, Opportunity, Sales, etc.).
 * @param {string} moduleType - The type of module (e.g., "lead", "opportunity", "sales").
 * @param {string} moduleId - The ID of the module.
 * @returns {Promise<Array>} - List of activities.
 */
export const fetchActivitiesByModule = async (moduleType, moduleId) => {
  try {
    const response = await apiClient.get(`${ACTIVITY_API_URL}/${moduleType}/${moduleId}`);
    return response.data || []; // Ensure it always returns an array
  } catch (error) {
    handleApiError(error);
  }
};

/**
 * Fetches the list of available activity types.
 * @returns {Promise<Array>} - List of activity types.
 */
export const fetchActivityTypes = async () => {
  try {
    const response = await apiClient.get(`${ACTIVITY_API_URL}/types`);
    return response.data || []; // Ensure it returns an array
  } catch (error) {
    handleApiError(error);
  }
};

export const fetchActivitiesByLead = async (leadId) => {
  try {
    const response = await apiClient.get(`${ACTIVITY_API_URL}/lead/${leadId}`);
    return response.data || [];
  } catch (error) {
    console.error("Error fetching activities:", error);
    return [];
  }
};

export const fetchActivitiesByOpportunity = async (opportunityId) => {
  try {
    const response = await apiClient.get(`${ACTIVITY_API_URL}/opportunity/${opportunityId}`);
    return response.data || [];
  } catch (error) {
    console.error("Error fetching activities:", error);
    return [];
  }
};
/**
 * Fetches a single activity by its ID.
 * @param {string} activityId - The ID of the activity.
 * @returns {Promise<Object>} - The activity details.
 */
export const fetchActivityById = async (activityId) => {
  try {
    const response = await apiClient.get(`${ACTIVITY_API_URL}/get/${activityId}`);
    return response.data || null; // Ensure it returns null if no data
  } catch (error) {
    handleApiError(error);
  }
};